// UserContext.js
import React, { createContext, useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { getSasToken } from '../../services/apiService'; // if needed for fallback refresh

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const { accounts, instance } = useMsal();
    const [userName, setUserName] = useState('');
    const [userId, setUserId] = useState('');
    const [role, setRole] = useState('');
    const [sasToken, setSasToken] = useState('');
    const [sasTokenExpiry, setSasTokenExpiry] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Load user data and SAS token info from localStorage
        const storedUserName = localStorage.getItem('userName');
        const storedUserId = localStorage.getItem('userId');
        const storedRole = localStorage.getItem('role');
        const storedSasToken = localStorage.getItem('sasToken');
        const storedSasTokenExpiry = localStorage.getItem('sasTokenExpiry');

        if (storedUserName && storedUserId && storedRole && storedSasToken && storedSasTokenExpiry) {
            setUserName(storedUserName);
            setUserId(storedUserId);
            setRole(storedRole);
            setSasToken(storedSasToken);
            setSasTokenExpiry(storedSasTokenExpiry);
            setIsAuthenticated(true);
        } else if (accounts.length > 0) {
            // Fallback if localStorage is empty but there is a valid MSAL account
            const account = accounts[0];
            const defaultRole = account.idTokenClaims?.roles?.[0] || 'Role.User';

            setUserName(account.name);
            setUserId(account.username);
            setRole(defaultRole);
            setIsAuthenticated(true);

            localStorage.setItem('userName', account.name);
            localStorage.setItem('userId', account.username);
            localStorage.setItem('role', defaultRole);

            // Optionally: fetch a new SAS token if one isn’t already stored
            (async () => {
                const token = await getSasToken();
                setSasToken(token);
                localStorage.setItem('sasToken', token);
                const tokenExpiry = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
                setSasTokenExpiry(tokenExpiry);
                localStorage.setItem('sasTokenExpiry', tokenExpiry);
            })();
        } else {
            setIsAuthenticated(false);
        }

        setLoading(false);
    }, [accounts]);

    // Check for SAS token expiration on load and when the stored expiry changes.
    useEffect(() => {
        if (sasTokenExpiry) {
            const expiryDate = new Date(sasTokenExpiry);
            const now = new Date();
            if (now > expiryDate) {
                // SAS token has expired: clear storage and sign out the user.
                localStorage.clear();
                setIsAuthenticated(false);
                instance.logoutRedirect({
                    postLogoutRedirectUri: 'https://oil.spm-automation.com/signin',
                });
            }
        }
    }, [sasTokenExpiry, instance]);

    return (
        <UserContext.Provider
            value={{
                userName,
                setUserName,
                userId,
                setUserId,
                role,
                setRole,
                sasToken,
                setSasToken,
                isAuthenticated,
                setIsAuthenticated,
                loading,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
